// Cross-audio volume management with automatic speech ducking

import { useCallback, useRef, RefObject, useEffect, useState } from 'react';
import { createLogger } from '@airhead-games/shared/utilities';
import type { WebAudioService } from '../services/audio/webAudioService';

const logger = createLogger('useMediaVolumeManager');

export interface VolumeSettings {
  musicVolume: number;
  musicVolumeDucked: number;
  sfxVolume: number;
  speechVolume: number;
}

export const DEFAULT_VOLUMES: VolumeSettings = {
  musicVolume: 1.0,
  musicVolumeDucked: 0.16,
  sfxVolume: 1.0,
  speechVolume: 1.0,
};

interface MuteSettings {
  isMuted: boolean;
}

export function useMediaVolumeManager(
  musicAudioRef: RefObject<HTMLAudioElement | null>,
  speechAudioRef: RefObject<HTMLAudioElement | null>,
  sfxAudioRef: RefObject<HTMLAudioElement | null>,
  isSpeaking: boolean,
  initialMuteSettings: Partial<MuteSettings> = {},
  webAudioService?: WebAudioService
) {
  // Starts at defaults; MediaProvider seeds the user's saved mix on room entry.
  const volumes = useRef<VolumeSettings>({ ...DEFAULT_VOLUMES });

  // State version of volumes to trigger re-renders in VolumeControl
  const [currentVolumes, setCurrentVolumes] = useState<VolumeSettings>({
    ...DEFAULT_VOLUMES,
  });

  const isDuckedManually = useRef<boolean>(false);

  // Track previous volume values to detect changes
  const previousVolumes = useRef<VolumeSettings>({ ...DEFAULT_VOLUMES });

  // Mute state management
  const [isMuted, setIsMuted] = useState<boolean>(
    initialMuteSettings.isMuted ?? true
  );

  // Toggle mute functions
  const toggleMute = useCallback(() => {
    setIsMuted((prev) => {
      logger.info(`Toggling mute: ${!prev}`);
      return !prev;
    });
  }, []);

  // Calculate target music volume
  const getTargetMusicVolume = useCallback(() => {
    // Don't set volume to 0 for global mute - let WebAudio gain node handle muting
    // This preserves audio signal for visualization while still muting output
    if (isSpeaking) return volumes.current.musicVolumeDucked; // Speech takes priority
    if (isDuckedManually.current) return volumes.current.musicVolumeDucked;
    return volumes.current.musicVolume;
  }, [isSpeaking]);

  // Apply volume to music
  const applyMusicVolume = useCallback(() => {
    const targetVolume = getTargetMusicVolume();

    // Set HTML audio element to full volume, control via WebAudio
    if (musicAudioRef.current) {
      musicAudioRef.current.volume = 1.0;
    }

    // Control actual volume via WebAudio service
    if (webAudioService) {
      webAudioService.setVolumes({ musicVolume: targetVolume });
    }

    // Only log if volume actually changed
    if (previousVolumes.current.musicVolume !== targetVolume) {
      logger.debug('Music volume changed', {
        from: previousVolumes.current.musicVolume,
        to: targetVolume,
        isSpeaking,
        isMuted,
      });
      previousVolumes.current.musicVolume = targetVolume;
    }
  }, [
    musicAudioRef,
    getTargetMusicVolume,
    isSpeaking,
    isMuted,
    webAudioService,
  ]);

  // Apply volume to speech
  const applySpeechVolume = useCallback(() => {
    // Always set HTML audio element to full volume for visualization
    if (speechAudioRef.current) {
      speechAudioRef.current.volume = 1.0;
    }

    // Control actual volume via WebAudio service, but respecting the slider
    const targetVolume = volumes.current.speechVolume;
    if (webAudioService) {
      webAudioService.setVolumes({ speechVolume: targetVolume });
    }

    // Only log if volume actually changed
    if (previousVolumes.current.speechVolume !== targetVolume) {
      logger.debug('Speech volume changed', {
        from: previousVolumes.current.speechVolume,
        to: targetVolume,
        isMuted,
      });
      previousVolumes.current.speechVolume = targetVolume;
    }
  }, [speechAudioRef, isMuted, webAudioService]);

  // Apply volume to SFX
  const applySfxVolume = useCallback(() => {
    const targetVolume = volumes.current.sfxVolume;

    // Set HTML audio element to full volume, control via WebAudio
    if (sfxAudioRef.current) {
      sfxAudioRef.current.volume = 1.0;
    }

    // Control actual volume via WebAudio service
    if (webAudioService) {
      webAudioService.setVolumes({ sfxVolume: targetVolume });
    }

    // Only log if volume actually changed
    if (previousVolumes.current.sfxVolume !== targetVolume) {
      logger.debug('SFX volume changed', {
        from: previousVolumes.current.sfxVolume,
        to: targetVolume,
        isMuted,
      });
      previousVolumes.current.sfxVolume = targetVolume;
    }
  }, [sfxAudioRef, isMuted, webAudioService]);

  // Halve quiet music and cap louder music at the established speech level.
  const applyDuckVolume = useCallback(() => {
    const currentMusicVolume = volumes.current.musicVolume;
    const currentDuckVolume = volumes.current.musicVolumeDucked;
    const newDuckVolume = Math.min(
      currentMusicVolume / 2,
      DEFAULT_VOLUMES.musicVolumeDucked
    );

    // Update duck volume if it changed
    if (newDuckVolume !== currentDuckVolume) {
      volumes.current.musicVolumeDucked = newDuckVolume;
      setCurrentVolumes({ ...volumes.current }); // Update state to trigger re-renders
      logger.info('Duck volume updated', {
        from: currentDuckVolume,
        to: newDuckVolume,
        musicVolume: currentMusicVolume,
      });
    }
  }, []);

  // Update volume settings (called by VolumeControl component)
  const updateVolumeSettings = useCallback(
    (newVolumes: Partial<VolumeSettings>) => {
      const updatedVolumes = { ...volumes.current, ...newVolumes };

      volumes.current = updatedVolumes;
      setCurrentVolumes(updatedVolumes); // Update state to trigger re-renders
      applyDuckVolume(); // Apply duck volume logic first
      applyMusicVolume(); // Reapply with new settings
      applySpeechVolume(); // Apply speech volume
      applySfxVolume(); // Apply SFX volume
      logger.info('Volume settings updated', newVolumes);
    },
    [applyDuckVolume, applyMusicVolume, applySpeechVolume, applySfxVolume]
  );

  // Duck music manually (speech ducking is automatic via isSpeaking)
  const duckMusic = useCallback(() => {
    if (!isDuckedManually.current) {
      isDuckedManually.current = true;
      applyMusicVolume();
      logger.info('Music ducked manually');
    }
  }, [applyMusicVolume]);

  const unduckMusic = useCallback(() => {
    if (isDuckedManually.current) {
      isDuckedManually.current = false;
      applyMusicVolume();
      logger.info('Music unducked manually');
    }
  }, [applyMusicVolume]);

  // Auto-apply volume when isSpeaking or isMuted changes
  useEffect(() => {
    applyDuckVolume();
    applyMusicVolume();
    applySpeechVolume();
    applySfxVolume();
  }, [applyDuckVolume, applyMusicVolume, applySpeechVolume, applySfxVolume]);

  return {
    // Mute state and controls
    isMuted,
    setIsMuted,
    toggleMute,

    // Volume management
    updateVolumeSettings,
    getTargetMusicVolume,

    // Manual ducking
    duckMusic,
    unduckMusic,

    // Current volume settings (for VolumeControl component)
    currentVolumes,
  };
}
